import { 
  PoundSterling, TrendingUp, Shield, BarChart3, Calculator, CreditCard,
  Brain, Zap, Target, Lock, CheckCircle, Globe, Database, Clock
} from 'lucide-react';
import { ProductFeatureConfig } from './types';

export const financialSolutionsConfigs: Record<string, ProductFeatureConfig> = {
  'accounting-management-suite': {
    keyBenefits: [
      {
        icon: PoundSterling,
        title: 'Financial Accuracy Enhancement',
        description: 'Achieve 99.9% accuracy in financial reporting with automated reconciliation and error detection.',
        metrics: '99.9% accuracy',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Cash Flow Optimization',
        description: 'Improve cash flow visibility and forecasting, reducing late payments by 75% through automation.',
        metrics: '75% payment improvement',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Financial Insights',
        description: 'Access comprehensive financial dashboards with real-time P&L, balance sheets, and KPI tracking.',
        metrics: 'Real-time reporting',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Calculator,
        title: 'Automated Bookkeeping',
        description: 'Reduce manual bookkeeping time by 85% with intelligent transaction categorization and reconciliation.',
        metrics: '85% time saved',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Shield,
        title: 'Compliance Assurance',
        description: 'Ensure GAAP compliance with automated audit trails and regulatory reporting capabilities.',
        metrics: 'GAAP compliant',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CreditCard,
        title: 'Multi-Currency Support',
        description: 'Handle global transactions with automatic currency conversion and exchange rate management.',
        metrics: 'Global currency support',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Forecasting',
        description: 'Machine learning algorithms provide accurate financial forecasting with 95% precision for budget planning.',
        metrics: '95% forecast accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Invoice Processing',
        description: 'OCR and AI technology automatically process invoices, reducing data entry time by 90%.',
        metrics: '90% faster processing',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Expense Analytics',
        description: 'Advanced expense categorization and analysis that identifies cost-saving opportunities automatically.',
        metrics: 'Cost optimization',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Cash Flow',
        description: 'AI models predict cash flow patterns and identify potential shortfalls 90 days in advance.',
        metrics: '90-day predictions',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: BarChart3,
        title: 'Custom Financial Reports',
        description: 'Generate sophisticated financial reports with drag-and-drop builder and automated distribution.',
        metrics: 'Custom reporting',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Calculator,
        title: 'Tax Optimization Engine',
        description: 'Automated tax calculation and optimization strategies that maximize deductions and minimize liability.',
        metrics: 'Tax optimization',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance ensuring maximum data protection and regulatory adherence.',
        metrics: 'Multi-compliance certified',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with comprehensive monitoring dashboards.',
        metrics: 'Real-time security insights',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of users with auto-scaling security measures.',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with advanced firewall rules and real-time DDoS attack prevention.',
        metrics: 'AI threat protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, and hardware token support with granular access controls.',
        metrics: 'Enterprise authentication',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat intelligence, and automated incident response.',
        metrics: 'Advanced SIEM',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup with 99.999% durability, and instant recovery capabilities.',
        metrics: '99.999% data durability',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and anomaly detection for enhanced security.',
        metrics: 'Advanced network security',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning and compliance validation with continuous security improvement.',
        metrics: 'Continuous security',
        color: 'from-orange-500 to-red-600'
      }
    ]
  },
  'invoice-management-system': {
    keyBenefits: [
      {
        icon: PoundSterling,
        title: 'Payment Acceleration',
        description: 'Reduce average payment time from 45 to 15 days with automated follow-ups and payment reminders.',
        metrics: '66% faster payments',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Zap,
        title: 'Invoice Processing Speed',
        description: 'Process invoices 10x faster with OCR technology and automated approval workflows.',
        metrics: '10x faster processing',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Cash Flow Visibility',
        description: 'Real-time visibility into outstanding invoices, aging reports, and payment forecasting.',
        metrics: 'Real-time visibility',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Error Reduction',
        description: 'Eliminate 95% of manual data entry errors with intelligent OCR and validation rules.',
        metrics: '95% error reduction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: CreditCard,
        title: 'Multiple Payment Options',
        description: 'Accept payments via ACH, credit cards, wire transfers, and digital wallets for customer convenience.',
        metrics: 'Multiple payment methods',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Collection Efficiency',
        description: 'Improve collection rates by 40% with automated dunning processes and payment plan management.',
        metrics: '40% better collections',
        color: 'from-red-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Smart Invoice Recognition',
        description: 'AI-powered OCR technology accurately extracts data from any invoice format with 99% accuracy.',
        metrics: '99% OCR accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Automated Approval Routing',
        description: 'Intelligent routing system automatically sends invoices to appropriate approvers based on business rules.',
        metrics: 'Smart routing',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Duplicate Detection',
        description: 'Advanced algorithms automatically detect and prevent duplicate invoice processing and payments.',
        metrics: 'Duplicate prevention',
        color: 'from-blue-500 to-green-600'
      },
      {
        icon: BarChart3,
        title: 'Predictive Analytics',
        description: 'Machine learning models predict payment behaviors and optimize collection strategies.',
        metrics: 'Payment predictions',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Calculator,
        title: 'Dynamic Pricing & Discounts',
        description: 'Automated early payment discounts and late payment fees based on customizable business rules.',
        metrics: 'Dynamic pricing',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: PoundSterling,
        title: 'Multi-Entity Management',
        description: 'Handle complex organizational structures with inter-company billing and consolidation capabilities.',
        metrics: 'Multi-entity support',
        color: 'from-teal-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, and GDPR compliance ensuring maximum data protection and regulatory adherence.',
        metrics: 'Multi-compliance certified',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis and security posture optimization with comprehensive monitoring dashboards.',
        metrics: 'Real-time security insights',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of users with auto-scaling security measures.',
        metrics: 'Enterprise scalability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with advanced firewall rules and real-time DDoS attack prevention.',
        metrics: 'AI threat protection',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, and hardware token support with granular access controls.',
        metrics: 'Enterprise authentication',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat intelligence, and automated incident response.',
        metrics: 'Advanced SIEM',
        color: 'from-teal-500 to-green-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup with 99.999% durability, and instant recovery capabilities.',
        metrics: '99.999% data durability',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with continuous network monitoring and anomaly detection for enhanced security.',
        metrics: 'Advanced network security',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning and compliance validation with continuous security improvement.',
        metrics: 'Continuous security',
        color: 'from-orange-500 to-red-600'
      }
    ]
  }
};